import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { REMMI_MFA_TRUSTED_DEVICE_STORAGE_KEY } from '@constant/remmi-trusted-device';
import { Crypto } from './encryption-utils';
import { LocalStorageService } from './local-storage.service';
import { isPublicReportLocation } from './public-report-route';

/**
 * Storage key for the scoped public report-viewer JWT. Deliberately DIFFERENT from the real
 * login `token` key so opening a public `/report-view` link can never overwrite — and thereby
 * corrupt — a user's actual Remmi session.
 */
export const REPORT_PUBLIC_TOKEN_STORAGE_KEY = 'report_public_token';

@Injectable()
export class TokenService {
  constructor(
    private router: Router,
    private localStorageService: LocalStorageService
  ) {}

  /**
   * Saves the token to local storage
   * @param token the token to save
   */
  setToken(token: string) {
    this.localStorageService.SetItem('token', Crypto.encrypt(token));
  }

  /**
   * Retrieves the token from local storage.
   *
   * Returns null unless the stored value decrypts to something that is STRUCTURALLY a JWT.
   * `Crypto.decrypt` cannot fail: when AES yields nothing (value written by a build with a
   * different `secretKey`, or truncated by Safari's ITP storage eviction) its last fallback
   * returns the raw ciphertext string. That garbage used to flow into `jwt_decode`, which
   * throws — and `isTokenExpired`/the auth interceptor call it without a try/catch, so the
   * throw escaped mid-request and left the page spinning forever. Filtering here makes every
   * downstream token read total.
   */
  getToken(): string | null {
    const decrypted = Crypto.decrypt(this.localStorageService.GetItem('token'));
    return this.asJwt(decrypted);
  }

  /**
   * User id carried by the stored access token, or null.
   *
   * Available the moment login stores the token, unlike the `uid` in localStorage which is only
   * written once `/me` answers — and logout wipes it. Opening the WebSocket off `uid` alone
   * therefore silently skipped the connect for anyone who logged back in without a page reload.
   */
  getUserIdFromToken(): string | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    const payload = this.payload(token);
    const id = payload?.user?.user_id ?? payload?.user_id ?? null;
    return typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id) ? id : null;
  }

  /** Narrows an arbitrary decrypted value to a structurally valid JWT string, else null. */
  private asJwt(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const token = value.trim();
    // header.payload.signature — and the payload must actually be decodable base64url JSON.
    if (token.split('.').length !== 3) {
      return null;
    }
    return this.payload(token) ? token : null;
  }

  /**
   * Removes the token from local storage
   */
  removeToken() {
    this.localStorageService.ClearAllItems([REMMI_MFA_TRUSTED_DEVICE_STORAGE_KEY]);
  }

  /**
   * Scoped public report-viewer token. Stored under a DEDICATED key — never the shared `token`
   * key used by the real login — so a public `/report-view` session cannot masquerade as a real
   * login (`isUserloggedIn()` reads only the shared key) or lock the user out of signing in.
   * Returns null when absent, undecryptable, or expired.
   */
  getPublicReportToken(): string | null {
    const raw = this.localStorageService.GetItem(REPORT_PUBLIC_TOKEN_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const token = this.asJwt(Crypto.decrypt(raw));
      return token && !this.isTokenExpired(token) ? token : null;
    } catch {
      return null;
    }
  }

  setPublicReportToken(token: string) {
    this.localStorageService.SetItem(REPORT_PUBLIC_TOKEN_STORAGE_KEY, Crypto.encrypt(token));
  }

  clearPublicReportToken() {
    this.localStorageService.RemoveItem(REPORT_PUBLIC_TOKEN_STORAGE_KEY);
  }

  /**
   * Checks if the token is valid
   * @returns true if the token is valid, false otherwise
   */
  private isTokenValid() {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    try {
      // Accept any decodable, non-expired login JWT regardless of the host that issued it.
      // Previously this pinned the token's `iss` claim to the frontend BASE_URL
      // (`environment.nodeApiUrl` + `/auth/login`). Against a local backend (e.g.
      // http://localhost:5001/api) the minted token carries a different `iss` host, so the
      // strict equality failed: login returned 200 and stored the token, but the route guard
      // (isUserloggedIn -> isTokenValid) then treated the user as logged-out and bounced back
      // to /login instead of the dashboard. The backend still validates the token on every
      // request, so dropping the host pin here only fixes the client-side redirect gating.
      return !!this.payload(token) && !this.isTokenExpired(token);
    } catch {
      return false;
    }
  }

  /**
   * Retrieves the payload from the given token
   * @param token the token to retrieve the payload from
   * @returns the payload if it exists, null otherwise
   */
  private payload(token: string): any | null {
    const [, payload] = token.split('.');
    return this.decodeTokenPayload(payload);
  }

  /**
   * Decodes the given token payload. Returns null instead of throwing: JWT payloads are
   * base64URL (`-`/`_`, no padding), which bare `atob` rejects, and a corrupted storage value
   * can be anything at all. Callers treat null as "not a usable token".
   */
  private decodeTokenPayload(payload: any): any | null {
    if (typeof payload !== 'string' || !payload) {
      return null;
    }
    try {
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  /**
   * Checks if the user is logged in
   * @returns true if the user is logged in, false otherwise
   */
  isUserloggedIn() {
    return this.isTokenValid();
  }

  /**
   * Retrieves the expiration date of the given token
   * @param token the token to retrieve the expiration date from
   * @returns the expiration date if it exists, null otherwise
   */
  /**
   * @returns a Date when the token carries a usable `exp`;
   *          `undefined` when the token decodes but has NO `exp` claim (treated as never expiring,
   *          preserving the original behaviour for such tokens);
   *          `null` when the token cannot be decoded at all.
   */
  private getTokenExpirationDate(token: string): Date | null | undefined {
    // `jwt_decode` throws ("Invalid token specified") on anything that is not a well-formed
    // JWT. That used to propagate out of `isTokenExpired` -> `AuthTokenInterceptor.intercept`,
    // where nothing catches it, so the HTTP request neither fired nor errored and the page hung.
    let decoded: any;
    try {
      decoded = jwt_decode(token);
    } catch {
      return null;
    }
    if (!decoded) {
      return null;
    }
    if (decoded.exp === undefined || decoded.exp === null) {
      return undefined;
    }
    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return isNaN(date.valueOf()) ? null : date;
  }

  /**
   * Checks if the given token is expired. Never throws — an unreadable token counts as expired.
   * @param token the token to check
   * @returns true if the token is expired, missing, or unreadable
   */
  isTokenExpired(token?: string): boolean {
    if (!token) {
      token = this.getToken();
    }
    if (!token) {
      return true;
    }
    const date = this.getTokenExpirationDate(token);
    // Decodable but no `exp` claim -> never expires (unchanged from the original intent; the old
    // code expressed this as `date === undefined` while the helper returned `null`, so it never
    // actually matched and fell through to `null.valueOf()` -> TypeError).
    if (date === undefined) {
      return false;
    }
    // Undecodable -> treat as expired. On a public report link that just makes the visitor
    // anonymous and shows the PIN gate, which is the safe outcome.
    if (date === null) {
      return true;
    }
    return !(date.valueOf() > new Date().valueOf());
  }

  /**
   * Handles the response from the server after a login
   * @param response the response to handle
   * @returns the token if it exists, null otherwise
   */
  handleTokenResponse(response: any) {
    if (response.access_token) {
      this.setToken(response.access_token);
      return response.access_token;
    } else {
      this.tokenError();
    }
  }

  /**
   * Called when there is an error with the token.
   *
   * Never bounces a public report visitor to `/login`: `/report-view/:slug` links are emailed to
   * customers who have no Remmi account, so a token problem there must leave them on the PIN gate.
   */
  tokenError() {
    if (isPublicReportLocation(this.router?.url)) {
      this.clearPublicReportToken();
      return;
    }
    this.removeToken();
    this.router.navigateByUrl('/login');
  }
}
